import { Check, ChevronsUpDown, LayoutGrid } from "lucide-react";
import { useNavigate } from "react-router";
import { ROUTES } from "@/shared/constants/routes";
import type { ResolvedWorkspace } from "@/shared/hooks/useWorkspaces";
import { cn } from "@/shared/ui/cn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { Icon } from "@/shared/ui/icon";

interface WorkspaceStripProps {
  current: ResolvedWorkspace;
  available: readonly ResolvedWorkspace[];
  collapsed: boolean;
  drawer?: boolean;
  /** End the menu with "Add modules…" — `useWorkspaces().addModules`, decided there, never here. */
  addModules?: boolean;
  onNavigate?: (() => void) | undefined;
}

/**
 * The products this organisation actually has, behind one compact selector.
 *
 * **Only what is owned.** A tenant with one workspace gets no strip at all, and an add-on nobody has
 * bought is never drawn — not greyed, not dashed, not locked. ADR-0017's second addendum (2026-09-15) is
 * explicit that the switcher must never become a standing reminder of what they have not bought, and a row
 * of dashed tiles is exactly that. `useWorkspaces` has already filtered to live add-ons the caller may
 * open; this component draws what it is given and asks no questions about entitlements.
 *
 * **One way to the rest** (ADR-0017 addendum of 2026-10-01, second, "discovering a module"): below the
 * modules, a separator and a single "Add modules…" that opens Settings → Modules, where every module is
 * listed with its price and its one action. It is there only for a member who may manage the subscription
 * and only while some module is not held — and only inside the opened menu, which is what keeps it an
 * answer to "where are the others?" rather than an advert. The strip's own rule is unchanged: a tenant
 * with no add-ons has no switcher, and finds modules in Settings → Subscription and Settings → Modules.
 *
 * One workspace still gets no control. Two or eight use the same shape: one row naming where the
 * reader is, and a menu naming every place they can go. This avoids the old icon matrix wrapping to
 * a second line as soon as four products shared the 208 px content width, and it keeps unfamiliar
 * module icons from becoming a memory test.
 */
export function WorkspaceStrip({
  current,
  available,
  collapsed,
  drawer = false,
  addModules = false,
  onNavigate,
}: WorkspaceStripProps) {
  const navigate = useNavigate();
  if (available.length < 2) return null;
  const go = (to: string) => {
    void navigate(to);
    onNavigate?.();
  };
  const compact = collapsed && !drawer;

  return (
    <div className={cn("border-b border-border py-2", compact ? "px-2" : "px-3")}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Switch module. Current module: ${current.workspace.label}`}
            className={cn(
              "flex items-center rounded-md border border-border bg-surface text-left text-fg transition-colors duration-120 hover:bg-surface-hover",
              compact ? "size-8 justify-center" : "h-9 w-full gap-2 px-2.5",
            )}
          >
            <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-sm bg-accent-soft text-accent-fg">
              <Icon icon={current.workspace.icon} size={16} />
            </span>
            {!compact ? (
              <>
                <span className="min-w-0 flex-1 truncate text-cell font-medium">
                  {current.workspace.label}
                </span>
                <Icon icon={ChevronsUpDown} size={16} className="size-3.5 shrink-0 text-fg-faint" />
              </>
            ) : null}
          </button>
        </DropdownMenuTrigger>
        {/* Beside the rail on a desktop; under the row in the phone's drawer, which has no room beside it. */}
        <DropdownMenuContent side={drawer ? "bottom" : "right"} align="start" className="min-w-56">
          <DropdownMenuLabel>Switch module</DropdownMenuLabel>
          {available.map((entry) => {
            const active = entry.workspace.id === current.workspace.id;
            return (
              <DropdownMenuItem
                key={entry.workspace.id}
                onSelect={() => go(entry.home)}
                className={cn(active && "bg-accent-soft text-accent-fg")}
              >
                <Icon icon={entry.workspace.icon} size={16} className="text-fg-muted" />
                <span className="min-w-0 flex-1 truncate">{entry.workspace.label}</span>
                {active ? <Icon icon={Check} size={16} className="text-accent-fg" /> : null}
              </DropdownMenuItem>
            );
          })}
          {addModules ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => go(ROUTES.settingsTab("modules"))} className="text-fg-muted">
                <Icon icon={LayoutGrid} size={16} />
                <span className="min-w-0 flex-1 truncate">Add modules…</span>
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
